import { createContext, useEffect, useState, useCallback } from "react";
import { useMutation } from "@apollo/client";
import { client } from "../App";
import {
  requestNotificationPermission,
  onForegroundMessage,
  areNotificationsSupported,
} from "../utils/firebase";
import { SAVE_WEB_DEVICE_TOKEN } from "../graphql/userQueries";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [userToken, setUserToken] = useState(null);
  const [userData, setUserData] = useState({});
  const [webNotificationsEnabled, setWebNotificationsEnabled] = useState(false);

  const [saveWebDeviceToken] = useMutation(SAVE_WEB_DEVICE_TOKEN);

  // Register web push notifications
  const registerWebNotifications = useCallback(async () => {
    try {
      // Check if notifications are supported
      if (!areNotificationsSupported()) {
        console.log("Web notifications not supported in this browser");
        return;
      }

      // Request permission and get token
      const token = await requestNotificationPermission();

      if (token) {
        // Save token to backend
        await saveWebDeviceToken({
          variables: { webDeviceToken: token },
        });
        setWebNotificationsEnabled(true);
        console.log("Web push notifications enabled");

        // Set up foreground message handler
        onForegroundMessage((payload) => {
          console.log("Foreground notification received:", payload);
          // Show notification using browser API for foreground
          if (Notification.permission === "granted" && payload.notification) {
            new Notification(payload.notification.title, {
              body: payload.notification.body,
              icon: "/logo192.png",
            });
          }
        });
      }
    } catch (error) {
      console.error("Error registering web notifications:", error);
    }
  }, [saveWebDeviceToken]);

  const login = async (user) => {
    setIsLoading(true);
    setUserData(user);
    setUserToken(user.token);
    localStorage.setItem("userToken", user.token);
    localStorage.setItem("userInfo", JSON.stringify(user));
    // await AsyncStorage.setItem('userRefreshToken', user.refresh);
    setIsLoading(false);

    // Register for web push notifications after login
    // Small delay to ensure token is set
    setTimeout(() => {
      registerWebNotifications();
    }, 1000);

    return;
  };

  const logout = async () => {
    setIsLoading(true);
    setUserToken(null);
    setUserData(null);
    localStorage.removeItem("userToken");
    localStorage.removeItem("userInfo");
    // await AsyncStorage.removeItem('userRefreshToken');
    setIsLoading(false);
    client.clearStore();
  };

  const IsLoggedIn = async () => {
    try {
      setIsLoading(true);
      let userToken = localStorage.getItem("userToken");
      let userInfo = localStorage.getItem("userInfo");
      userInfo = JSON.parse(userInfo);
      if (userInfo) {
        setUserToken(userToken);
        setUserData(userInfo);
      }
      setIsLoading(false);
    } catch (error) {
      console.log(`isLogged in Error ${error}`);
    }
  };

  useEffect(() => {
    IsLoggedIn();
  }, []);

  // Register web notifications when user is already logged in
  useEffect(() => {
    if (userToken && !webNotificationsEnabled) {
      registerWebNotifications();
    }
  }, [userToken, webNotificationsEnabled, registerWebNotifications]);

  return (
    <AuthContext.Provider
      value={{
        login,
        logout,
        IsLoggedIn,
        isLoading,
        userToken,
        userData,
        webNotificationsEnabled,
        registerWebNotifications,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
